import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";

const WORD_LIMIT = 100;

function countWords(text) {
  return (text || "").trim().split(/\s+/).filter(Boolean).length;
}

function formatTimestamp(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function CommentsSection() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/comments");
      setComments(data.comments || []);
    } catch (e) {
      // silent; show no comments
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000); // light refresh
    return () => clearInterval(id);
  }, [load]);

  const wc = useMemo(() => countWords(body), [body]);
  const overLimit = wc > WORD_LIMIT;
  const canSubmit = !!user && !!user.has_paid && body.trim().length > 0 && !overLimit && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/comments", { body: body.trim() });
      setComments((prev) => [data.comment, ...prev]);
      setBody("");
      toast.success(t("comments_posted_ok"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleUpvote = async (commentId) => {
    if (!user) {
      toast.error(t("comment_upvote_login"));
      return;
    }
    // Snapshot the pre-optimistic state for clean rollback
    const snapshot = comments.find((c) => c.id === commentId);
    if (!snapshot) return;
    // Optimistic update
    setComments((prev) =>
      prev.map((c) =>
        c.id === commentId
          ? {
              ...c,
              user_has_upvoted: !c.user_has_upvoted,
              upvote_count: c.upvote_count + (c.user_has_upvoted ? -1 : 1),
            }
          : c
      )
    );
    try {
      const { data } = await api.post(`/comments/${commentId}/upvote`);
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, user_has_upvoted: data.upvoted, upvote_count: data.upvote_count }
            : c
        )
      );
    } catch (err) {
      // Restore the exact pre-optimistic snapshot
      setComments((prev) => prev.map((c) => (c.id === commentId ? snapshot : c)));
      toast.error(formatApiError(err));
    }
  };

  const total = comments.length;
  const totalLabel = total === 1 ? t("comments_total") : t("comments_total_plural");
  const totalText = totalLabel.replace("{n}", total.toString());
  const wordCountText = t("comments_word_count").replace("{n}", wc.toString());

  return (
    <section className="mt-14 border-t-2 border-black pt-10" data-testid="comments-section">
      <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black flex items-center gap-2" style={{ fontWeight: 900 }}>
        <span className="w-2 h-2 bg-[#E00000] rounded-full" />
        {t("comments_section_tag")}
      </div>
      <h2 className="mt-2 font-serif-display text-black leading-[0.95] text-4xl sm:text-5xl" style={{ fontWeight: 800 }} data-testid="comments-heading">
        {t("comments_h")}
      </h2>
      <p className="mt-3 text-black max-w-2xl text-sm sm:text-base" style={{ fontWeight: 600 }}>
        {t("comments_intro")}
      </p>

      {/* Composer */}
      <div className="mt-6 bg-white border-2 border-black p-5 sm:p-6 brutal-shadow-sm" data-testid="comments-composer">
        {!user ? (
          <div className="font-mono-civic text-xs uppercase tracking-widest text-black" style={{ fontWeight: 800 }}>
            {t("comments_login_required")}{" "}
            <Link to="/login" className="underline" data-testid="comments-login-link">{t("nav_login")}</Link>
            {" · "}
            <Link to="/register" className="underline" data-testid="comments-register-link">{t("nav_register")}</Link>
          </div>
        ) : !user.has_paid ? (
          <div className="font-mono-civic text-xs uppercase tracking-widest text-black" style={{ fontWeight: 800 }}>
            {t("comments_paywall_required")}{" "}
            <Link to="/paywall" className="underline" data-testid="comments-paywall-link">{t("nav_unlock")}</Link>
          </div>
        ) : (
          <form onSubmit={submit}>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t("comments_placeholder")}
              rows={4}
              maxLength={2000}
              className="w-full border-2 border-black p-3 text-black font-mono-civic text-sm focus:outline-none focus:ring-0 focus:bg-[#FFF8DC] resize-y"
              style={{ fontWeight: 600 }}
              data-testid="comments-input"
            />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div
                className={`font-mono-civic text-xs uppercase tracking-widest ${overLimit ? "text-[#E00000]" : "text-black/70"}`}
                style={{ fontWeight: 800 }}
                data-testid="comments-wordcount"
              >
                {overLimit ? t("comments_word_over") : wordCountText}
              </div>
              <button
                type="submit"
                disabled={!canSubmit}
                className="bg-black text-white uppercase tracking-widest px-5 py-2.5 border-2 border-black text-xs hover:bg-white hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ fontWeight: 900 }}
                data-testid="comments-submit"
              >
                {submitting ? t("comments_posting") : t("comments_post_btn")}
              </button>
            </div>
            <p className="mt-2 font-mono-civic text-[10px] uppercase tracking-widest text-black/60" style={{ fontWeight: 700 }}>
              {t("comments_civility_rules")}
            </p>
          </form>
        )}
      </div>

      {/* List */}
      <div className="mt-8 flex items-center justify-between">
        <div className="font-mono-civic text-xs uppercase tracking-widest text-black" style={{ fontWeight: 900 }} data-testid="comments-total">
          {totalText}
        </div>
      </div>

      {loading ? (
        <div className="mt-4 font-mono-civic text-xs uppercase tracking-widest text-black/60" style={{ fontWeight: 800 }}>
          {t("comments_loading")}
        </div>
      ) : comments.length === 0 ? (
        <div className="mt-4 border-2 border-dashed border-black p-6 text-center font-mono-civic text-xs uppercase tracking-widest text-black/60" style={{ fontWeight: 800 }} data-testid="comments-empty">
          {t("comments_empty")}
        </div>
      ) : (
        <ul className="mt-4 space-y-4" data-testid="comments-list">
          {comments.map((c) => (
            <li
              key={c.id}
              className="bg-white border-2 border-black p-4 sm:p-5"
              data-testid={`comment-${c.id}`}
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={() => toggleUpvote(c.id)}
                  disabled={!user}
                  aria-pressed={c.user_has_upvoted}
                  aria-label={t("comment_upvote_aria")}
                  className={`shrink-0 flex flex-col items-center justify-center min-w-[3rem] border-2 border-black px-2 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${c.user_has_upvoted ? "bg-[#E00000] text-white" : "bg-white text-black hover:bg-black hover:text-white"}`}
                  style={{ fontWeight: 900 }}
                  data-testid={`comment-upvote-${c.id}`}
                  title={!user ? t("comment_upvote_login") : ""}
                >
                  <span aria-hidden className="text-base leading-none">▲</span>
                  <span className="font-mono-civic text-xs tracking-widest mt-1" data-testid={`comment-upvote-count-${c.id}`}>
                    {c.upvote_count}
                  </span>
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <span className="font-mono-civic text-[10px] sm:text-xs uppercase tracking-widest text-black" style={{ fontWeight: 900 }}>
                      {c.author_email_masked}
                    </span>
                    <span className="font-mono-civic text-[10px] uppercase tracking-widest text-black/60" style={{ fontWeight: 800 }}>
                      {formatTimestamp(c.created_at, lang)}
                    </span>
                  </div>
                  <p className="text-black whitespace-pre-wrap break-words" style={{ fontWeight: 600 }}>
                    {c.body}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
